import { setupStyleSheet, type AppStyleSheet } from "./setupStyleSheet"
import type { StyleConfig } from "../types"
import WebFont from "webfontloader"

export const changeTheme = (theme: StyleConfig, styleSheet: AppStyleSheet | null) => {

  if (styleSheet) {
    setupStyleSheet(styleSheet)
    return
  }

  // Sets old CSS variables for old Look & Feel
  const root = document.documentElement
  root.style.setProperty("--aiden-primary-color", theme.primary)
  root.style.setProperty(
    "--aiden-primary-hover-brightness",
    hoverBrightness(theme.primary)
  )
  root.style.setProperty("--aiden-secondary-color", theme.focus)
  root.style.setProperty(
    "--aiden-secondary-hover-brightness",
    hoverBrightness(theme.focus)
  )
  root.style.setProperty("--aiden-text-color", theme.text)

  root.style.setProperty("--aiden-header-text-color", theme.headerColor)
  root.style.setProperty(
    "--aiden-button-padding-v",
    theme.buttonPaddingVertical
  )
  root.style.setProperty(
    "--aiden-button-padding-h",
    theme.buttonPaddingHorizontal
  )
  root.style.setProperty("--aiden-border-radius", theme.borderRadius)
  root.style.setProperty(
    "--aiden-border-radius-max",
    maxBorderRadius(theme.borderRadius)
  )
  root.style.setProperty("--aiden-border-radius-card", theme.borderRadiusCard)
  root.style.setProperty(
    "--aiden-border-radius-card-max",
    maxBorderRadius(theme.borderRadiusCard)
  )

  root.style.setProperty(
    "--aiden-border-radius-info-modal",
    maxBorderRadius(theme.borderRadiusCard, 1)
  )

  root.style.setProperty("--aiden-background-color", theme.background)
  root.style.setProperty(
    "--aiden-select-and-hover-color",
    selectionHue(theme.text)
  )
  root.style.setProperty(
    "--aiden-font-family",
    `"${theme.font}", -apple-system, BlinkMacSystemFont,
    "Segoe UI", Roboto, Oxygen-Sans, Ubuntu, Cantarell, "Helvetica Neue",
    sans-serif`
  )
  root.style.setProperty(
    "--aiden-header-font-family",
    `"${theme.fontHeader}", "${theme.font}", -apple-system, BlinkMacSystemFont,
    "Segoe UI", Roboto, Oxygen-Sans, Ubuntu, Cantarell, "Helvetica Neue",
    sans-serif`
  )
  root.style.setProperty(
    "--aiden-header-text-decoration",
    theme.header.includes("underline") ? "underline" : "normal"
  )
  root.style.setProperty(
    "--aiden-header-font-weight",
    theme.header.includes("bold") ? "bold" : "normal"
  )
  root.style.setProperty(
    "--aiden-header-font-style",
    theme.header.includes("italic") ? "italic" : "normal"
  )

  root.style.setProperty(
    "--aiden-primary-text-color",
    getContrastColor(theme.primary)
  )
  root.style.setProperty(
    "--aiden-primary-font-smoothing-webkit",
    getContrastColor(theme.primary) == "white" ? "antialiased" : "auto"
  )
  root.style.setProperty(
    "--aiden-primary-font-smoothing-moz",
    getContrastColor(theme.primary) == "white" ? "grayscale" : "auto"
  )

  root.style.setProperty(
    "--aiden-secondary-text-color",
    getContrastColor(theme.focus)
  )

  root.style.setProperty(
    "--aiden-text-on-bg-color",
    getContrastColor(theme.background)
  )

  root.style.setProperty(
    "--aiden-box-shadow-level-1",
    theme.boxShadowOn ? "0 2px 8px rgba(0, 0, 0, 0.1)" : "none"
  )
  root.style.setProperty(
    "--aiden-box-shadow-level-2",
    theme.boxShadowOn ? "0 6px 16px rgba(0, 0, 0, 0.1)" : "none"
  )

  const fontWeights = "400,700"
  const fontHeaderWeights = theme.header.includes("bold") ? "400,700" : "400"
  const families = [
    `${theme.font}:${fontWeights}`,
    `${theme.fontHeader}:${fontHeaderWeights}`,
  ]
  const params = families
    .map((f) => f.replace(" ", "+"))
    .join(
      "%7C" // %7C = | = pipe symbol
    )
  WebFont.load({
    // google: {
    //   families: [`${theme.font}:400,700`, `${theme.fontHeader}:400,700`],
    // },
    custom: {
      families: families,
      urls: [`https://fonts.bunny.net/css?family=${params}&display=swap`],
    },
  })
}

const colorToRgb = (color: string) => {
  let rgb: number[] = []

  if (color.startsWith("rgb")) {
    rgb = color.match(/\d+/g)?.map(Number) || []
  } else if (color.startsWith("#") && color.length == 4) {
    rgb = [
      parseInt(`${color[1]}${color[1]}`, 16),
      parseInt(`${color[2]}${color[2]}`, 16),
      parseInt(`${color[3]}${color[3]}`, 16),
    ]
  } else if (color.startsWith("#") && color.length == 7) {
    rgb = [
      parseInt(color.slice(1, 3), 16),
      parseInt(color.slice(3, 5), 16),
      parseInt(color.slice(5, 7), 16),
    ]
  }
  return rgb
}

const selectionHue = (color: string) => {
  const alpha = 0.05
  const rgb = colorToRgb(color)
  const [r, g, b] = rgb

  if (!rgb.length) {
    return "#FAFAFA"
  } else {
    return `rgba(${r}, ${g}, ${b}, ${alpha})`
  }
}

export const getContrastColor = (color: string) => {
  const [r, g, b] = colorToRgb(color)
  /* The actual YIQ color space average is calculated like so:
     ((r * 299) + (g * 587) + (b * 114)) / 1000
     We reduce the weight or r by half to achieve white text on orange backgrounds,
     see: https://uxmovement.com/buttons/the-myths-of-color-contrast-accessibility/
  */
  const yiq = (r * 150 + g * 587 + b * 114) / 1000

  return yiq >= 128 ? "black" : "white"
}

const hoverBrightness = (color: string) => {
  return getContrastColor(color) === "black" ? "92%" : "110%"
}

const maxBorderRadius = (remValue: string, max = 0.5) => {
  const value = parseFloat(remValue)
  if (isNaN(value)) {
    return remValue
  } else if (value > max) {
    return `${max}rem`
  } else {
    return `${value}rem`
  }
}
